import { createRef, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import LdsIcon from '../LdsIcon/index.jsx';
export default function LdsImageSlider({
  id,
  initialIndex = 0,
  allowFullScreen = true,
  ariaLabel,
  images = [],
  maxWidth,
  imageAttributes,
  className,
  ...rest
}) {
  const imagesRef = useRef(images.map(() => createRef()));
  const captionsRef = useRef(images.map(() => createRef()));
  const dotsRef = useRef(images.map(() => createRef()));
  const [isExpanded, setIsExpanded] = useState(false);
  let currentImgIdx = initialIndex;

  const handleNavBtnClick = (next = 0) => {
    const totalSlides = images.length;
    let slideIndex = currentImgIdx;
    slideIndex += next;
    if (slideIndex >= totalSlides) { slideIndex = 0; }
    if (slideIndex < 0) { slideIndex = totalSlides - 1; }
    scrollToSlide(slideIndex);
  };

  const handleDotBtnClick = (index) => {
    scrollToSlide(index);
  };

  const scrollToSlide = (index) => {
    let slide = null;
    slide = imagesRef?.current[index]?.current;
    slide.scrollIntoView({
      behavior: 'smooth',
      inline: 'start',
      block: 'nearest',
    });
    setActiveIndex(index);
  };

  const setActiveIndex = (index) => {
    currentImgIdx = index;
    captionsRef?.current.forEach(el => el.current.classList.remove('active'));
    dotsRef?.current.forEach(el => el.current.classList.remove('active'));
    captionsRef?.current[index].current.classList.add('active');
    dotsRef?.current[index].current.classList.add('active');
  };

  const toggleExpanded = () => {
    setIsExpanded(expanded => !expanded);
  };

  const ImageSliderFullScreenToggle = () => {
    return (
      <div className="lds-image-slider-full-screen">
        <button
          className="lds-button-base lds-image-slider-full-screen-toggle focus-3px"
          aria-label="Expand image to full size"
          onClick={() => toggleExpanded()}
        >
          { isExpanded
            ? <LdsIcon name="ArrowsInSimple" decorative inline></LdsIcon>
            : <LdsIcon name="ArrowsOutSimple" decorative inline></LdsIcon>
          }
        </button>
      </div>
    );
  };

  const ImageSliderNavButtons = () => {
    return (
      <div className="lds-image-slider-navs">
        <button className="lds-button-base lds-image-slider-prev" aria-label="Previous Slide" onClick={() => handleNavBtnClick(-1)} >
          <LdsIcon name="CaretLeft" decorative inline></LdsIcon>
        </button>
        <button className="lds-button-base lds-image-slider-next" aria-label="Next Slide" onClick={() => handleNavBtnClick(1)}>
          <LdsIcon name="CaretRight" decorative inline></LdsIcon>
        </button>
      </div>
    );
  };

  const ImageSliderDots = () => {
    return (
      <div className="lds-image-slider-dots">
        {
          images.map((img, index) => (
            <button
              id={`image-slider-dots-${index + 1}`}
              ref={dotsRef.current[index]}
              key={`image-slider-dots-${index}`}
              className={cn(
                initialIndex === index ? 'active' : '',
                'lds-button-clear-style',
                'focus-3px'
              )}
              onClick={() => handleDotBtnClick(index)}
            >
              {`Display ${index + 1}`}
            </button>
          ))
        }
      </div>
    );
  };

  const ImageSliderCaptions = () => {
    return (
      <div
        className="lds-image-slider-captions text-center"
        aria-live="polite"
      >
        {
          images.map((img, index) => (
            <p
              key={`image-slider-captions-${index}`}
              ref={captionsRef.current[index]}
              className={initialIndex === index ? 'active' : ''}
            >
              <span className="sr-only">
                {`Slide ${index + 1} ${img.alt}`}
              </span>
              {img.caption ? img.caption : '\u00A0' }
            </p>
          ))
        }
      </div>
    );
  };

  const ImageSliderImages = () => {
    return (
      <div className="lds-image-slider-body">
        <div className="lds-image-slider-frame">
          {
            images.map((img, index) => (
              <div
                key={`image-slider-images-${index}`}
                ref={imagesRef.current[index]}
                className="lds-image-slider-frame-segment"
                id={`image-item-${index + 1}`}
                aria-labelledby={`image-slider-item-${index + 1}`}
              >
                <img
                  className={cn({ 'w-100': maxWidth && !isExpanded })}
                  src={img.src}
                  alt={img.alt ? img.alt : `Slide ${index + 1}`}
                  style={img.style || null}
                  {...(!isExpanded ? imageAttributes : [])}
                >
                </img>
              </div>
            ))
          }
        </div>
      </div>
    );
  };

  return (
    <div
      id={id}
      className={cn(
        'lds-image-slider',
        {
          expanded: isExpanded,
        },
        className
      )}
      role="region"
      aria-roledescription="image slides"
      aria-label={ariaLabel}
      style={{ maxWidth: maxWidth && !isExpanded ? maxWidth + 'px' : '' }}
      {...rest}
    >
      <ImageSliderImages />
      { allowFullScreen && <ImageSliderFullScreenToggle /> }
      <ImageSliderNavButtons />
      <ImageSliderDots />
      <ImageSliderCaptions />
    </div>
  );
}

LdsImageSlider.propTypes = {
  id: PropTypes.string.isRequired,
  allowFullScreen: PropTypes.bool,
  ariaLabel: PropTypes.string,
  initialIndex: PropTypes.number,
  images: PropTypes.array.isRequired,
  maxWidth: PropTypes.number,
};
